/*
 * Copyright © 2024-2026 symecode.com. All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, modification, or distribution is prohibited.
 */


import axios, { AxiosError, AxiosInstance } from 'axios';
import {
  LoginRequest,
  LoginResponse,
  CreateParticipantRequest,
  UpdateParticipantRequest,
  ParticipantResponse,
  ParticipantListResponse,
  ChangePasswordRequest,
  CreateBidRequest,
  UpdateBidRequest,
  BidResponse,
  BidListResponse,
  AddComponentToBidRequest,
  BidBodyResponse,
  BidBodyListResponse,
  GenerateComponentRequest,
  GenerateComponentResponse,
  ComponentResponse,
  ComponentListResponse,
  ComponentCollectionItem,
  CompanyListResponse,
  CompanyResponse,
  CompanyRequest,
  HealthResponse,
  ApiResponse,
  UpdateComponentRequest,
  MonthlyReportResponse,
  MonthlyDetailResponse,
  ReassignBidRequest,
  CompanyCalendarResponse,
  Bid,
  ExecutiveYearlyActivityResponse,
  ExecutiveMonthlyBidSummaryResponse
} from '../types';

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: '/api',
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true,
    });

    // Request interceptor to add JWT token
    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('jwt_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        if (error.response?.status === 401) {
          // Unauthorized - clear token and redirect to login
          localStorage.removeItem('jwt_token');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Authentication endpoints
  async login(request: LoginRequest): Promise<LoginResponse> {
    const response = await this.client.post<LoginResponse>('/auth/login', request);
    // Store the JWT token in localStorage
    if (response.data.success && response.data.token) {
      localStorage.setItem('jwt_token', response.data.token);
    }
    return response.data;
  }

  async logout(): Promise<ApiResponse> {
    const response = await this.client.post<ApiResponse>('/auth/logout');
    // Clear the JWT token from localStorage
    localStorage.removeItem('jwt_token');
    return response.data;
  }

  async getCurrentUser(): Promise<ParticipantResponse> {
    const response = await this.client.get<ParticipantResponse>('/auth/current-user');
    return response.data;
  }

  // Participant endpoints
  async createParticipant(request: CreateParticipantRequest): Promise<ParticipantResponse> {
    const response = await this.client.post<ParticipantResponse>('/participants', request);
    return response.data;
  }

  async createUser(request: CreateParticipantRequest): Promise<ParticipantResponse> {
    const response = await this.client.post<ParticipantResponse>('/participants/users', request);
    return response.data;
  }

  async getAllParticipants(): Promise<ParticipantListResponse> {
    const response = await this.client.get<ParticipantListResponse>('/participants');
    return response.data;
  }

  async getParticipantById(uuid: string): Promise<ParticipantResponse> {
    const response = await this.client.get<ParticipantResponse>(`/participants/${uuid}`);
    return response.data;
  }

  async changePassword(uuid: string, request: ChangePasswordRequest): Promise<ApiResponse> {
    const response = await this.client.put<ApiResponse>(`/participants/${uuid}/password`, request);
    return response.data;
  }

  async deleteParticipant(uuid: string): Promise<ApiResponse> {
    const response = await this.client.delete<ApiResponse>(`/participants/${uuid}`);
    return response.data;
  }

  async updateParticipant(uuid: string, request: UpdateParticipantRequest): Promise<ParticipantResponse> {
    const response = await this.client.put<ParticipantResponse>(`/participants/${uuid}`, request);
    return response.data;
  }

  async getManagedUsers(): Promise<ParticipantListResponse> {
    const response = await this.client.get<ParticipantListResponse>('/participants/managed-users');
    return response.data;
  }

  async getManagedUserById(uuid: string): Promise<ParticipantResponse> {
    const response = await this.client.get<ParticipantResponse>(`/participants/managed-users/${uuid}`);
    return response.data;
  }

  async updateManagedUser(uuid: string, request: UpdateParticipantRequest): Promise<ParticipantResponse> {
    const response = await this.client.put<ParticipantResponse>(`/participants/managed-users/${uuid}`, request);
    return response.data;
  }

  async getMyProfile(): Promise<ParticipantResponse> {
    const response = await this.client.get<ParticipantResponse>('/participants/me');
    return response.data;
  }

  async updateMyProfile(request: UpdateParticipantRequest): Promise<ParticipantResponse> {
    const response = await this.client.put<ParticipantResponse>('/participants/me', request);
    return response.data;
  }

  // Bid endpoints
  async createBid(request: CreateBidRequest): Promise<BidResponse> {
    const response = await this.client.post<BidResponse>('/bids', request);
    return response.data;
  }

  async getAllBids(): Promise<BidListResponse> {
    const response = await this.client.get<BidListResponse>('/bids');
    return response.data;
  }

  async getRecentlyModifiedBids(): Promise<BidListResponse> {
    const response = await this.client.get<BidListResponse>('/bids/recent');
    return response.data;
  }

  async getBidById(id: number): Promise<BidResponse> {
    const response = await this.client.get<BidResponse>(`/bids/${id}`);
    return response.data;
  }

  async updateBid(id: number, request: UpdateBidRequest): Promise<BidResponse> {
    const response = await this.client.put<BidResponse>(`/bids/${id}`, request);
    return response.data;
  }

  async reassignBid(id: number, request: ReassignBidRequest): Promise<ApiResponse> {
    const response = await this.client.put<ApiResponse>(`/bids/${id}/reassign`, request);
    return response.data;
  }

  async bulkReassignBids(bidIds: number[], newAuthorId: number): Promise<ApiResponse> {
    const response = await this.client.put<ApiResponse>('/bids/bulk-reassign', {
      bidIds,
      newAuthorId
    });
    return response.data;
  }

  async deleteBid(id: number): Promise<ApiResponse> {
    const response = await this.client.delete<ApiResponse>(`/bids/${id}`);
    return response.data;
  }

  async getAdminBidById(id: number): Promise<BidResponse> {
    const response = await this.client.get<BidResponse>(`/admin/bids/${id}`);
    return response.data;
  }

  async getManagerBidById(id: number): Promise<BidResponse> {
    const response = await this.client.get<BidResponse>(`/manager/bids/${id}`);
    return response.data;
  }

  async getBidVersions(id: number): Promise<{ success: boolean; bidGroupId: number; versions: Bid[] }> {
    const response = await this.client.get<{ success: boolean; bidGroupId: number; versions: Bid[] }>(`/bids/${id}/versions`);
    return response.data;
  }

  async addComponentToBid(request: AddComponentToBidRequest): Promise<BidBodyResponse> {
    const response = await this.client.post<BidBodyResponse>('/bids/add-component', request);
    return response.data;
  }

  async removeComponentFromBid(bidBodyId: number): Promise<ApiResponse> {
    const response = await this.client.delete<ApiResponse>(`/bids/remove-component/${bidBodyId}`);
    return response.data;
  }

  async getBidBodies(bidId: number): Promise<BidBodyListResponse> {
    const response = await this.client.get<BidBodyListResponse>(`/bids/${bidId}/bid-bodies`);
    return response.data;
  }

  async updateBidBodySequence(bidBodyId: number, sequenceNumber: number): Promise<BidBodyResponse> {
    const response = await this.client.put<BidBodyResponse>(
      `/bids/bid-bodies/${bidBodyId}/sequence`,
      { sequenceNumber }
    );
    return response.data;
  }

  // PDF endpoints
  async uploadComponent(file: File, manufacturerName?: string, manufacturerCode?: string): Promise<ComponentResponse> {
    const formData = new FormData();
    formData.append('file', file);
    if (manufacturerName) {
      formData.append('manufacturerName', manufacturerName);
    }
    if (manufacturerCode) {
      formData.append('manufacturerCode', manufacturerCode);
    }

    const response = await this.client.post<ComponentResponse>('/component/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async uploadComponentWithDetails(formData: FormData): Promise<ComponentResponse> {
    const response = await this.client.post<ComponentResponse>('/component/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async getAllComponents(): Promise<ComponentListResponse> {
    const response = await this.client.get<ComponentListResponse>('/component');
    return response.data;
  }

  async getComponentById(id: number): Promise<ComponentResponse> {
    const response = await this.client.get<ComponentResponse>(`/component/${id}`);
    return response.data;
  }

  async deleteComponent(id: number): Promise<ApiResponse> {
    const response = await this.client.delete<ApiResponse>(`/component/${id}`);
    return response.data;
  }

  async updateComponent(id: number, data: UpdateComponentRequest | FormData): Promise<ComponentResponse> {
    const headers = data instanceof FormData ? { 'Content-Type': 'multipart/form-data' } : {};
    const response = await this.client.put<ComponentResponse>(`/component/${id}`, data, { headers });
    return response.data;
  }

  async generateComponent(request: GenerateComponentRequest): Promise<GenerateComponentResponse> {
    const response = await this.client.post<GenerateComponentResponse>('/component/generate', request);
    return response.data;
  }

  async finalizeBid(bidId: number): Promise<GenerateComponentResponse> {
    const response = await this.client.post<GenerateComponentResponse>(`/component/finalize/${bidId}`);
    return response.data;
  }

  async generateBidPackage(bidId: number): Promise<void> {
    const token = localStorage.getItem('jwt_token');

    // Fetch the PDF with authentication
    const response = await fetch(`/api/component/generate-bid-package/${bidId}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to generate PDF package: ${response.statusText} - ${errorText}`);
    }

    // Get the filename from the Content-Disposition header
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = 'bid_package.pdf';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1];
      }
    }

    // Create a blob from the response
    const blob = await response.blob();

    // Create a blob URL and trigger download
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up the blob URL
    setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
  }

  async downloadBidPdf(bidId: number): Promise<void> {
    const token = localStorage.getItem('jwt_token');

    // Fetch the PDF with authentication
    const response = await fetch(`/api/bids/download-bid-pdf/${bidId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to download PDF: ${response.statusText} - ${errorText}`);
    }

    // Get the filename from the Content-Disposition header
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = 'bid_package.pdf';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename=\"?(.+)\"?/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1];
      }
    }

    // Create a blob from the response
    const blob = await response.blob();

    // Create a blob URL and trigger download
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up the blob URL
    setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
  }

  async searchComponents(searchTerm: string): Promise<ComponentListResponse> {
    const response = await this.client.get<ComponentListResponse>('/component/search', {
      params: { searchTerm },
    });
    return response.data;
  }

  async getComponentHealth(): Promise<HealthResponse> {
    const response = await this.client.get<HealthResponse>('/component/health');
    return response.data;
  }

  // Component Collection endpoints
  async getCollectionItems(collectionId: number): Promise<{ success: boolean; items: ComponentCollectionItem[] }> {
    const response = await this.client.get<{ success: boolean; items: ComponentCollectionItem[] }>(
      `/component/${collectionId}/items`
    );
    return response.data;
  }

  async addItemToCollection(collectionId: number, componentId: number, suggestedCount: number = 1): Promise<{ success: boolean; item: ComponentCollectionItem }> {
    const response = await this.client.post<{ success: boolean; item: ComponentCollectionItem }>(
      `/manager/collections/${collectionId}/items`,
      { componentId, suggestedCount }
    );
    return response.data;
  }

  async removeItemFromCollection(itemId: number): Promise<{ success: boolean; message: string }> {
    const response = await this.client.delete<{ success: boolean; message: string }>(
      `/manager/collections/items/${itemId}`
    );
    return response.data;
  }

  async updateCollectionItemCount(itemId: number, suggestedCount: number): Promise<{ success: boolean; item: ComponentCollectionItem }> {
    const response = await this.client.patch<{ success: boolean; item: ComponentCollectionItem }>(
      `/manager/collections/items/${itemId}/count`,
      { suggestedCount }
    );
    return response.data;
  }

  async moveCollectionItemUp(itemId: number): Promise<{ success: boolean; item: ComponentCollectionItem }> {
    const response = await this.client.patch<{ success: boolean; item: ComponentCollectionItem }>(
      `/manager/collections/items/${itemId}/move-up`
    );
    return response.data;
  }

  async moveCollectionItemDown(itemId: number): Promise<{ success: boolean; item: ComponentCollectionItem }> {
    const response = await this.client.patch<{ success: boolean; item: ComponentCollectionItem }>(
      `/manager/collections/items/${itemId}/move-down`
    );
    return response.data;
  }

  // Template Item endpoints
  async getTemplateItems(collectionId: number): Promise<{ success: boolean; items: any[] }> {
    const response = await this.client.get<{ success: boolean; items: any[] }>(
      `/template-items/collection/${collectionId}`
    );
    return response.data;
  }

  async createTemplateItem(collectionId: number, data: { templatePattern: string; dataType: string; defaultValue?: string }): Promise<{ success: boolean; item: any }> {
    const response = await this.client.post<{ success: boolean; item: any }>(
      `/template-items/collection/${collectionId}`,
      data
    );
    return response.data;
  }

  async updateTemplateItem(id: number, data: { templatePattern: string; dataType: string; defaultValue?: string }): Promise<{ success: boolean; item: any }> {
    const response = await this.client.put<{ success: boolean; item: any }>(
      `/template-items/${id}`,
      data
    );
    return response.data;
  }

  async deleteTemplateItem(id: number): Promise<{ success: boolean; message: string }> {
    const response = await this.client.delete<{ success: boolean; message: string }>(
      `/template-items/${id}`
    );
    return response.data;
  }

  // Company endpoints
  async getAllCompanies(): Promise<CompanyListResponse> {
    const response = await this.client.get<CompanyListResponse>('/admin/companies');
    return response.data;
  }

  async getCompanyById(id: number): Promise<CompanyResponse> {
    const response = await this.client.get<CompanyResponse>(`/admin/companies/${id}`);
    return response.data;
  }

  async createCompany(request: CompanyRequest): Promise<CompanyResponse> {
    const response = await this.client.post<CompanyResponse>('/admin/companies', request);
    return response.data;
  }

  async updateCompany(id: number, request: CompanyRequest): Promise<CompanyResponse> {
    const response = await this.client.put<CompanyResponse>(`/admin/companies/${id}`, request);
    return response.data;
  }

  async deleteCompany(id: number): Promise<ApiResponse> {
    const response = await this.client.delete<ApiResponse>(`/admin/companies/${id}`);
    return response.data;
  }

  // Manager company endpoints
  async getManagerCompany(): Promise<CompanyResponse> {
    const response = await this.client.get<CompanyResponse>('/manager/company');
    return response.data;
  }

  async updateManagerCompany(request: CompanyRequest): Promise<CompanyResponse> {
    const response = await this.client.put<CompanyResponse>('/manager/company', request);
    return response.data;
  }

  // Fetch and open component file in a new window
  async viewComponentFile(id: number): Promise<void> {
    const token = localStorage.getItem('jwt_token');
    const url = `/api/component/${id}/file`;

    // Fetch the component file with authentication
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch component file: ${response.statusText}`);
    }

    // Create a blob from the response
    const blob = await response.blob();

    // Create a blob URL and open it in a new window
    const blobUrl = URL.createObjectURL(blob);
    window.open(blobUrl, '_blank');

    // Clean up the blob URL after a short delay
    setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
  }

  // Get monthly activity report
  async getMonthlyActivityReport(): Promise<MonthlyReportResponse> {
    const response = await this.client.get<MonthlyReportResponse>('/admin/companies/monthly-report');
    return response.data;
  }

  // Get manager activity report
  async getManagerActivityReport(): Promise<MonthlyReportResponse> {
    const response = await this.client.get<MonthlyReportResponse>('/admin/companies/manager-report');
    return response.data;
  }

  // Get monthly detail for a specific month (admin)
  async getMonthlyDetail(yearMonth: string): Promise<MonthlyDetailResponse> {
    const response = await this.client.get<MonthlyDetailResponse>(`/admin/companies/monthly-detail/${yearMonth}`);
    return response.data;
  }

  // Get manager monthly detail for a specific month
  async getManagerMonthlyDetail(yearMonth: string): Promise<MonthlyDetailResponse> {
    const response = await this.client.get<MonthlyDetailResponse>(`/admin/companies/manager-detail/${yearMonth}`);
    return response.data;
  }

  // Password reset endpoints
  async forgotPassword(username: string): Promise<ApiResponse> {
    const response = await this.client.post<ApiResponse>('/auth/forgot-password', { username });
    return response.data;
  }

  async validateResetToken(token: string): Promise<ApiResponse & { username?: string }> {
    const response = await this.client.get<ApiResponse & { username?: string }>(`/auth/validate-reset-token?token=${token}`);
    return response.data;
  }

  async resetPassword(token: string, newPassword: string): Promise<ApiResponse> {
    const response = await this.client.post<ApiResponse>('/auth/reset-password', { token, newPassword });
    return response.data;
  }

  // Bid Results
  async getBidResultByBidId(bidId: number): Promise<ApiResponse & { bidResult?: any }> {
    const response = await this.client.get<ApiResponse & { bidResult?: any }>(`/bid-results/by-bid/${bidId}`);
    return response.data;
  }

  async saveBidResult(bidResult: any): Promise<ApiResponse & { bidResult?: any }> {
    const response = await this.client.post<ApiResponse & { bidResult?: any }>('/bid-results', bidResult);
    return response.data;
  }

  // Company Calendar endpoints
  async getCompanyCalendar(year: number, month: number): Promise<CompanyCalendarResponse> {
    const response = await this.client.get<CompanyCalendarResponse>(`/company/calendar/${year}/${month}`);
    return response.data;
  }

  async getCurrentMonthCalendar(): Promise<CompanyCalendarResponse> {
    const response = await this.client.get<CompanyCalendarResponse>('/company/calendar/current');
    return response.data;
  }

  // Executive Report endpoints
  async getExecutiveYearlyActivity(year: number): Promise<ExecutiveYearlyActivityResponse> {
    const response = await this.client.get<ExecutiveYearlyActivityResponse>(`/executive/activity/${year}`);
    return response.data;
  }

  async getExecutiveMonthlyBidSummary(year: number, month: number, category: string): Promise<ExecutiveMonthlyBidSummaryResponse> {
    const response = await this.client.get<ExecutiveMonthlyBidSummaryResponse>(`/executive/summary/${year}/${month}/${category}`);
    return response.data;
  }

  // Payment endpoints
  async checkCredits(bidId: number): Promise<ApiResponse & { creditCheck?: any }> {
    const response = await this.client.get<ApiResponse & { creditCheck?: any }>(`/payments/check-credits/${bidId}`);
    return response.data;
  }

  async createPaymentIntent(amount: number, currency: string, description: string): Promise<ApiResponse & { clientSecret?: string; paymentIntentId?: string; publishableKey?: string }> {
    const response = await this.client.post<ApiResponse & { clientSecret?: string; paymentIntentId?: string; publishableKey?: string }>('/payments/create-payment-intent', {
      amount,
      currency,
      description
    });
    return response.data;
  }

  async confirmPayment(paymentIntentId: string): Promise<ApiResponse & { payment?: any; newCreditBalance?: number; newCreditBalanceFormatted?: string }> {
    const response = await this.client.post<ApiResponse & { payment?: any; newCreditBalance?: number; newCreditBalanceFormatted?: string }>('/payments/confirm-payment', {
      paymentIntentId
    });
    return response.data;
  }

  async getStripeConfig(): Promise<{ publishableKey: string }> {
    const response = await this.client.get<{ publishableKey: string }>('/payments/config');
    return response.data;
  }
}

// Export a singleton instance
export const apiClient = new ApiClient();
export default apiClient;
